import React, { useMemo, useState, useRef } from 'react';
import { FileText, Trophy, ExternalLink } from 'lucide-react';
import { jsonrepair } from 'jsonrepair';

/* ═══════════════════════════════════════════════════
   Types — matches GET /api/concept-test-vitamin/{id}
   ═══════════════════════════════════════════════════ */

interface KScore {
  mean: number;
  pct: number;
  std: number;
  benchmark: number;
  delta: number;
  verdict: 'pass' | 'warn' | 'fail';
  distribution?: Record<string, number>;
  n?: number;
}

interface SegmentData {
  name: string;
  description: string;
  n_agents: number;
  metrics: Record<string, KScore>;
  top_strength: { metric: string; label: string };
  primary_friction: { metric: string; label: string };
}

interface FrictionItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
}

interface DriverItem {
  rank: number;
  name: string;
  pct: number;
  count: number;
  bar_width?: number;
}

interface InsightCard {
  headline: string;
  body: string;
}

interface VitaminReport {
  test_id: string;
  concept: Record<string, any>;
  population: string;
  n_agents: number;
  timestamp: string;
  overall_composite_pct: number;
  k_scores: Record<string, KScore>;
  segments: Record<string, SegmentData>;
  friction_drivers: {
    frictions: FrictionItem[];
    drivers: DriverItem[];
    quotes?: string[];
  };
  agent_results?: Record<string, any>;
  generated?: {
    verdict_text: string;
    segment_insights: Record<string, string>;
    friction_driver_descriptions: Record<string, string>;
    overall_insights: {
      cards: InsightCard[];
      strongest_signal?: string;
      biggest_risk?: string;
    };
  };
  _config?: Record<string, any>;
}

/* ═══════════════════════════════════════════════════
   Multi-Concept Types
   ═══════════════════════════════════════════════════ */

interface MultiConceptWinner {
  product_headline: string;
  brand_name: string;
  overall_mean: number;
  why_winner_won: string;
  winner_verbatim_summary: string;
  product_image_url?: string;
}

interface ConceptRanking {
  rank: number;
  product_headline: string;
  brand_name: string;
  price_point?: string;
  overall_mean: number;
  k1_mean?: number;
  k2_mean?: number;
  k3_mean?: number;
  k4_mean?: number;
  top_2_box?: Record<string, number>;
  report_html?: string;
}

interface MultiConceptComparison {
  friction_drivers: string;
  overall_insights: string;
}

interface MultiConceptConceptData {
  index: number;
  product_headline: string;
  brand_name: string;
  price_point?: string;
  product_image_url?: string;
  overall_mean: number;
  n_agents?: number;
  k_scores: Record<string, KScore>;
  top_2_box?: Record<string, number>;
  friction_drivers: {
    frictions: FrictionItem[];
    drivers: DriverItem[];
    quotes?: string[];
  };
  segments?: Record<string, SegmentData>;
  report_html?: string;
  report_json?: string;
  // API does return generated data for each concept in multi-concept tests
  generated?: {
    verdict_text: string;
    segment_insights: Record<string, string>;
    friction_driver_descriptions: Record<string, string>;
    overall_insights: {
      cards: InsightCard[];
      strongest_signal: string;
      biggest_risk: string;
    };
  };
}

interface MultiConceptResponse {
  test_id: string;
  mode: 'multi';
  n_concepts: number;
  population: string;
  winner: MultiConceptWinner;
  ranking: ConceptRanking[];
  comparison: MultiConceptComparison;
  concepts: MultiConceptConceptData[];
}

type ParsedReport =
  | { mode: 'single'; data: VitaminReport }
  | { mode: 'multi'; data: MultiConceptResponse };

/* ═══════════════════════════════════════════════════
   Brand palette (BluePill)
   ═══════════════════════════════════════════════════ */

const BRAND = {
  accent: '#07E5D1',
  accentLight: '#E8FCFA',
  accentHover: '#06D4C2',
  dark: '#012e2a',
};

// Colors for differentiating concepts with same name
const CONCEPT_COLORS = ['#07E5D1', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Helper function to calculate Top 2 Box percentage from distribution
const calculateTop2Box = (score: { distribution?: Record<string, number> } | undefined): number => {
  if (!score?.distribution) return 0;
  const dist = score.distribution;
  const top2 = (dist['5'] || 0) + (dist['4'] || 0);
  const total = Object.values(dist).reduce((sum, v) => sum + v, 0);
  return total > 0 ? Math.round((top2 / total) * 100) : 0;
};

const K_LABELS: Record<string, string> = {
  k1: 'Purchase Intent',
  k2: 'Value Perception',
  k3: 'New & Different',
  k4: 'Believability',
};

const K_ORDER = ['k1', 'k2', 'k3', 'k4'];

const VERDICT_STYLE: Record<string, { bg: string; border: string; textClass: string; label: string; dotClass: string }> = {
  pass: { bg: 'bg-teal-50', border: 'border-teal-200', textClass: 'text-teal-700', label: 'above category norm', dotClass: 'bg-teal-500' },
  warn: { bg: 'bg-amber-50', border: 'border-amber-200', textClass: 'text-amber-700', label: 'at category norm — watch', dotClass: 'bg-amber-500' },
  fail: { bg: 'bg-red-50', border: 'border-red-200', textClass: 'text-red-700', label: 'below category norm', dotClass: 'bg-red-500' },
};

/* ═══════════════════════════════════════════════════
   Parse helper
   ═══════════════════════════════════════════════════ */

function parseReport(raw: any): ParsedReport | null {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(jsonrepair(raw)) : raw;

    // Check for multi-concept response
    if (data.mode === 'multi' || (data.results && data.results.mode === 'multi')) {
      const multiData = data.results || data;
      return { mode: 'multi', data: multiData as MultiConceptResponse };
    }

    // Single concept response
    if (data.k_scores && data.overall_composite_pct !== undefined) {
      return { mode: 'single', data: data as VitaminReport };
    }
    if (data.results && data.results.k_scores) {
      return { mode: 'single', data: data.results as VitaminReport };
    }

    // Fallback - try to parse as single
    if (data.k_scores || data.overall_composite_pct !== undefined) {
      return { mode: 'single', data: data as VitaminReport };
    }

    return null;
  } catch {
    return null;
  }
}

/* ═══════════════════════════════════════════════════
   Section Label
   ═══════════════════════════════════════════════════ */

const SectionLabel: React.FC<{ num: string; label: string }> = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-7">
    <span className="text-xs font-bold tracking-wider font-mono tabular-nums" style={{ color: BRAND.accent }}>{num}</span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: BRAND.dark }}>{label}</span>
    <div className="flex-1 h-px" style={{ backgroundColor: `${BRAND.accent}30` }} />
  </div>
);

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */

interface Props {
  simulationResponse: any;
  contentData: any;
  isPreparingExport?: boolean;
}

const VitaminConceptTestResults: React.FC<Props> = ({ simulationResponse, contentData }) => {
  const parsed = useMemo(() => parseReport(simulationResponse), [simulationResponse]);

  if (!parsed) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse concept test results.</p>
      </div>
    );
  }

  // Multi-concept mode
  if (parsed.mode === 'multi') {
    return <MultiConceptResultsView data={parsed.data} contentData={contentData} />;
  }

  // Single-concept mode
  const report = parsed.data;
  const displayName = (contentData?.name as string) || report?.concept?.brand_name || 'Concept Report';

  if (report.overall_composite_pct === undefined) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p>Unable to parse concept test results.</p>
      </div>
    );
  }

  return <SingleConceptResultsView report={report} displayName={displayName} />;
};

/* ═══════════════════════════════════════════════════
   SINGLE CONCEPT RESULTS VIEW
   ═══════════════════════════════════════════════════ */

interface SingleConceptResultsViewProps {
  report: VitaminReport;
  displayName: string;
}

const SingleConceptResultsView: React.FC<SingleConceptResultsViewProps> = ({ report, displayName }) => {
  const kKeys = K_ORDER.filter(k => report.k_scores[k]);
  const segEntries = Object.entries(report.segments || {})
    .map(([id, seg]) => ({ id, ...seg }))
    .sort((a, b) => (b.metrics?.k1?.mean ?? 0) - (a.metrics?.k1?.mean ?? 0));

  return (
    <div className="space-y-10">
      <CoverSection report={report} displayName={displayName} />
      <ExecSummary report={report} kKeys={kKeys} />
      {segEntries.length > 0 && <SegmentBreakdown report={report} kKeys={kKeys} segEntries={segEntries} />}
      <FrictionAndDrivers report={report} />
      {report.generated?.overall_insights?.cards && <OverallInsights report={report} />}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MULTI-CONCEPT RESULTS VIEW
   ═══════════════════════════════════════════════════ */

interface MultiConceptResultsViewProps {
  data: MultiConceptResponse;
  contentData: any;
}

const MultiConceptResultsView: React.FC<MultiConceptResultsViewProps> = ({ data, contentData }) => {
  const [activeReportIndex, setActiveReportIndex] = useState(0);
  const individualReportsSectionRef = useRef<HTMLDivElement>(null);

  const displayName = (contentData?.name as string) || 'Multi-Concept Comparison';

  // Find the winner index by matching both brand_name AND product_headline to handle duplicate names
  const winnerIndex = useMemo(() => {
    const idx = data.concepts.findIndex(
      c => c.brand_name === data.winner.brand_name && c.product_headline === data.winner.product_headline
    );
    // Fallback: if not found by both, try finding by rank 1 in ranking
    if (idx === -1 && data.ranking.length > 0) {
      const winnerRanking = data.ranking.find(r => r.rank === 1);
      if (winnerRanking) {
        return data.concepts.findIndex(
          c => c.brand_name === winnerRanking.brand_name && c.product_headline === winnerRanking.product_headline
        );
      }
    }
    return idx !== -1 ? idx : 0;
  }, [data.concepts, data.winner, data.ranking]);

  // Handler that sets active tab and scrolls to the individual reports section
  const handleViewDetails = (index: number) => {
    setActiveReportIndex(index);
    // Scroll to the individual reports section after a small delay to allow state update
    setTimeout(() => {
      individualReportsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="space-y-10">
      {/* Cover Section - Multi */}
      <MultiConceptCoverSection data={data} displayName={displayName} />

      {/* Section 01: Winner & Rankings */}
      <WinnerHighlightSection
        winner={data.winner}
        ranking={data.ranking}
        concepts={data.concepts}
        winnerIndex={winnerIndex}
        onViewDetails={handleViewDetails}
      />

      {/* Section 02: Friction & Drivers Comparison */}
      <ComparisonFrictionDrivers
        comparison={data.comparison}
        concepts={data.concepts}
        winnerIndex={winnerIndex}
      />

      {/* Section 03: Overall Comparison Insights */}
      <ComparisonOverallInsights comparison={data.comparison} />

      {/* Section 04: Individual Reports */}
      <IndividualReportsSection
        ref={individualReportsSectionRef}
        concepts={data.concepts}
        activeIndex={activeReportIndex}
        onTabChange={setActiveReportIndex}
        winnerIndex={winnerIndex}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MULTI-CONCEPT COVER SECTION
   ═══════════════════════════════════════════════════ */

const MultiConceptCoverSection: React.FC<{ data: MultiConceptResponse; displayName: string }> = ({ data, displayName }) => {
  const totalAgents = data.concepts.reduce((sum, c) => sum + (c.n_agents || 0), 0) || 100;
  const stats = [
    { value: `N=${totalAgents}`, label: 'Consumer twins' },
    { value: String(data.n_concepts), label: 'Concepts tested' },
    { value: 'Comparison', label: 'Study type' },
    { value: String(Object.keys(data.concepts[0]?.k_scores || {}).length || 4), label: 'Core metrics' },
  ];

  // Find the winning concept to get its product image
  const winningConcept = data.concepts.find(
    c => c.product_headline === data.winner.product_headline
  );
  const winnerImageUrl = winningConcept?.product_image_url || data.winner.product_image_url;
  const hasWinnerImage = winnerImageUrl && winnerImageUrl.trim() !== '';

  return (
    <div className="rounded-lg shadow-sm overflow-hidden relative" style={{ backgroundColor: BRAND.dark }}>
      {/* Decorative arcs */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 600, height: 600, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}12`,
          top: -200, right: -150,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 400, height: 400, borderRadius: '50%',
          border: `1px solid ${BRAND.accent}08`,
          top: -80, right: -50,
        }}
      />

      <div className="px-8 pt-8 pb-6 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-10">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full"
              style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
            >
              BluePill AI
            </span>
            <span className="text-xs text-white/40 tracking-wide">× Multi-Concept Test</span>
          </div>
          <div className="text-[11px] text-white/35 text-right leading-relaxed">
            {data.test_id && <>Study ID: BP-MCT-{data.test_id.slice(0, 6)}<br /></>}
            Version: 1.0 — Final
          </div>
        </div>

        {/* Title block with optional winner image */}
        <div className={`mb-12 ${hasWinnerImage ? 'flex gap-8' : ''}`}>
          <div className={hasWinnerImage ? 'flex-1' : ''}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">CPG Multi-Concept Comparison</div>
            <h2 className="text-3xl font-semibold text-white leading-tight mb-3">
              Concept Comparison<br />
              <em className="font-normal text-white/40">& Winner Analysis</em>
            </h2>
            <p className="text-sm text-white/50 leading-relaxed max-w-2xl">
              <strong className="text-white/80">{displayName}</strong>
              {' — '}Comparing {data.n_concepts} concepts to identify the strongest performer
            </p>
          </div>

          {/* Winner Product Image */}
          {hasWinnerImage && (
            <div className="flex-shrink-0 flex flex-col items-center">
              <div
                className="w-40 h-40 rounded-lg overflow-hidden flex items-center justify-center"
                style={{
                  border: `2px solid ${BRAND.accent}40`,
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }}
              >
                <img
                  src={winnerImageUrl}
                  alt={data.winner.product_headline}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <Trophy className="h-3 w-3" style={{ color: BRAND.accent }} />
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-medium">Winner</span>
              </div>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="flex gap-12 pt-6" style={{ borderTop: `1px solid ${BRAND.accent}18` }}>
          {stats.map(s => (
            <div key={s.label}>
              <div className="text-4xl font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.accent }}>{s.value}</div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-8 py-3 flex items-center justify-between gap-4 relative z-10" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] text-white/25 leading-relaxed">
          Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. Multi-concept comparison with winner analysis.
        </p>
        <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 01: Winner & Rankings
   ═══════════════════════════════════════════════════ */

interface WinnerHighlightSectionProps {
  winner: MultiConceptWinner;
  ranking: ConceptRanking[];
  concepts: MultiConceptConceptData[];
  winnerIndex: number;
  onViewDetails: (index: number) => void;
}

const WinnerHighlightSection: React.FC<WinnerHighlightSectionProps> = ({
  winner,
  ranking,
  concepts,
  winnerIndex,
  onViewDetails,
}) => {
  // Use winnerIndex to get the winning concept for k1 (Purchase Intent) score
  const winnerConcept = concepts[winnerIndex];
  const winnerK1Mean = winnerConcept?.k_scores?.k1?.mean ?? winner.overall_mean;

  return (
    <div>
      <SectionLabel num="01" label="Winner & Rankings" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          The winning <span className="italic font-normal" style={{ color: BRAND.accent }}>concept.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Based on Purchase Intent score. Rankings show relative performance of each concept tested.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Winner Hero Card */}
        <div className="rounded-lg p-8" style={{ backgroundColor: BRAND.dark }}>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6" style={{ color: BRAND.accent }} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-white/50">
              Winning Concept
            </span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">{winner.brand_name}</h3>
          <p className="text-sm text-white/60 mb-6">{winner.product_headline}</p>
          <div className="text-6xl font-bold leading-none mb-1" style={{ color: BRAND.accent }}>
            {winnerK1Mean.toFixed(1)}
            <span className="text-lg text-white/40 ml-2 font-normal">/ 5.0</span>
          </div>
          <div className="text-xs text-white/40">purchase intent</div>
        </div>

        {/* Ranked List */}
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
            All Concepts Ranked
          </div>
          {ranking.map((concept, idx) => {
            const isWinner = concept.rank === 1;
            // Find concept data by matching both brand_name AND product_headline to handle duplicates
            const conceptIdx = concepts.findIndex(
              c => c.brand_name === concept.brand_name && c.product_headline === concept.product_headline
            );
            const conceptData = conceptIdx !== -1 ? concepts[conceptIdx] : undefined;
            const k1Mean = conceptData?.k_scores?.k1?.mean ?? concept.k1_mean ?? concept.overall_mean;
            return (
              <div
                key={idx}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                  isWinner
                    ? 'border-teal-200 bg-teal-50/50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isWinner ? 'text-white' : 'text-gray-600 bg-gray-100'
                  }`}
                  style={isWinner ? { backgroundColor: BRAND.accent } : {}}
                >
                  {concept.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm">{concept.brand_name}</div>
                  <div className="text-xs text-gray-500 truncate">{concept.product_headline}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold" style={{ color: isWinner ? BRAND.dark : '#374151' }}>
                    {k1Mean.toFixed(1)}
                  </div>
                  <div className="text-[10px] text-gray-400">purchase intent</div>
                </div>
                <button
                  onClick={() => onViewDetails(conceptIdx !== -1 ? conceptIdx : idx)}
                  className="p-2 text-gray-400 hover:text-teal-600 transition-colors"
                  title="View full report"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Winner Won */}
      {winner.why_winner_won && (
        <div className="mt-6 p-5 rounded-lg" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}30` }}>
          <h4 className="text-sm font-semibold mb-2" style={{ color: BRAND.dark }}>
            Why This Concept Won
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {winner.why_winner_won}
          </p>
        </div>
      )}

      {/* Verbatim Summary */}
      {winner.winner_verbatim_summary && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Consumer Verbatims
          </h4>
          <p className="text-sm text-gray-700 italic leading-relaxed">
            &ldquo;{winner.winner_verbatim_summary}&rdquo;
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 02: Comparison Friction & Drivers
   ═══════════════════════════════════════════════════ */

interface ComparisonFrictionDriversProps {
  comparison: MultiConceptComparison;
  concepts: MultiConceptConceptData[];
  winnerIndex: number;
}

const ComparisonFrictionDrivers: React.FC<ComparisonFrictionDriversProps> = ({
  comparison,
  concepts,
  winnerIndex,
}) => {
  // Detect duplicate brand names to show color indicators
  const brandNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    concepts.forEach(c => {
      const name = c.brand_name || 'Concept';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [concepts]);

  const hasDuplicates = Object.values(brandNameCounts).some(count => count > 1);

  // Determine grid columns based on number of concepts
  const gridColsClass = concepts.length === 2
    ? 'grid-cols-1 md:grid-cols-2'
    : concepts.length === 3
    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  return (
    <div>
      <SectionLabel num="02" label="Friction & Driver Comparison" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What drove and blocked <span className="italic font-normal" style={{ color: BRAND.accent }}>each concept.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Primary friction categories and purchase drivers for each concept tested.
        </p>
      </div>

      <div className={`grid ${gridColsClass} gap-4`}>
        {concepts.map((concept, idx) => {
          const brandName = concept.brand_name || `Concept ${idx + 1}`;
          const isWinner = idx === winnerIndex;
          const frictions = concept.friction_drivers?.frictions?.slice(0, 3) || [];
          const drivers = concept.friction_drivers?.drivers?.slice(0, 3) || [];
          const showColorIndicator = hasDuplicates && brandNameCounts[brandName] > 1;
          const conceptColor = CONCEPT_COLORS[idx % CONCEPT_COLORS.length];

          return (
            <div
              key={concept.index ?? idx}
              className={`rounded-lg p-5 ${
                isWinner
                  ? 'border-2'
                  : 'border border-gray-200 bg-white'
              }`}
              style={isWinner ? { borderColor: BRAND.accent, backgroundColor: BRAND.accentLight + '30' } : {}}
            >
              {/* Concept Header */}
              <div className="flex items-center gap-2 mb-4">
                {showColorIndicator && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: conceptColor }}
                    title={`Concept ${idx + 1}`}
                  />
                )}
                {isWinner && <Trophy className="w-4 h-4" style={{ color: BRAND.accent }} />}
                <span className="text-sm font-semibold truncate" style={{ color: isWinner ? BRAND.dark : '#374151' }}>
                  {brandName}
                </span>
                {isWinner && <span className="text-[10px] text-teal-600 flex-shrink-0">(Winner)</span>}
              </div>

              {/* Frictions */}
              <div className="mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-2">
                  Top Frictions
                </div>
                {frictions.length > 0 ? (
                  <div className="space-y-1.5">
                    {frictions.map((f, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm">
                        <span className="text-red-500 font-medium min-w-[32px]">{f.pct}%</span>
                        <span className="text-gray-700 text-xs leading-tight">{f.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No friction data</p>
                )}
              </div>

              {/* Drivers */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: isWinner ? BRAND.dark : '#6B7280' }}>
                  Top Drivers
                </div>
                {drivers.length > 0 ? (
                  <div className="space-y-1.5">
                    {drivers.map((d, dIdx) => (
                      <div key={dIdx} className="flex items-center gap-2 text-sm">
                        <span className="font-medium min-w-[32px]" style={{ color: isWinner ? BRAND.accent : '#6B7280' }}>{d.pct}%</span>
                        <span className="text-gray-700 text-xs leading-tight">{d.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No driver data</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Narrative */}
      {comparison.friction_drivers && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Comparison Insight
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {comparison.friction_drivers}
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 03: Overall Comparison Insights
   ═══════════════════════════════════════════════════ */

const ComparisonOverallInsights: React.FC<{ comparison: MultiConceptComparison }> = ({ comparison }) => {
  if (!comparison.overall_insights) return null;

  return (
    <div>
      <SectionLabel num="03" label="Overall Insights" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Cross-concept <span className="italic font-normal" style={{ color: BRAND.accent }}>patterns.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Key insights from comparing all concepts in this study.
        </p>
      </div>

      <div className="rounded-lg p-6" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}35` }}>
        <p className="text-[15px] text-gray-800 leading-relaxed">
          {comparison.overall_insights}
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 04: Individual Reports
   ═══════════════════════════════════════════════════ */

interface IndividualReportsSectionProps {
  concepts: MultiConceptConceptData[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  winnerIndex: number;
}

const IndividualReportsSection = React.forwardRef<HTMLDivElement, IndividualReportsSectionProps>(({
  concepts,
  activeIndex,
  onTabChange,
  winnerIndex,
}, ref) => {
  const activeConcept = concepts[activeIndex];

  if (!activeConcept) return null;

  const displayName = activeConcept.brand_name || `Concept ${activeIndex + 1}`;

  // Detect duplicate brand names to show color indicators
  const brandNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    concepts.forEach(c => {
      const name = c.brand_name || 'Concept';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [concepts]);

  // Check if any names are duplicated
  const hasDuplicates = Object.values(brandNameCounts).some(count => count > 1);

  return (
    <div ref={ref}>
      <SectionLabel num="04" label="Individual Reports" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Detailed concept <span className="italic font-normal" style={{ color: BRAND.accent }}>reports.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Dive into the full analysis for each concept tested.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 -mb-px">
          {concepts.map((concept, idx) => {
            const brandName = concept.brand_name || `Concept ${idx + 1}`;
            const isWinner = idx === winnerIndex;
            const isActive = activeIndex === idx;
            // Show color indicator if there are duplicate names
            const showColorIndicator = hasDuplicates && brandNameCounts[brandName] > 1;
            const conceptColor = CONCEPT_COLORS[idx % CONCEPT_COLORS.length];

            return (
              <button
                key={idx}
                onClick={() => onTabChange(idx)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  isActive
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {showColorIndicator && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: conceptColor }}
                    title={`Concept ${idx + 1}`}
                  />
                )}
                {isWinner && <Trophy className="w-3.5 h-3.5" style={{ color: BRAND.accent }} />}
                {brandName}
                {isWinner && <span className="text-[10px] text-teal-600">(Winner)</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Active Report Content - Multi-concept individual view */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <MultiConceptIndividualReport concept={activeConcept} displayName={displayName} />
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════════
   INDIVIDUAL REPORT FOR MULTI-CONCEPT
   Converts MultiConceptConceptData to VitaminReport format
   and reuses the same components as single-concept view
   ═══════════════════════════════════════════════════ */

const MultiConceptIndividualReport: React.FC<{ concept: MultiConceptConceptData; displayName: string }> = ({
  concept,
  displayName,
}) => {
  // Convert MultiConceptConceptData to VitaminReport format
  // so we can reuse the same components as single-concept view
  const report: VitaminReport = useMemo(() => {
    const segEntries = Object.entries(concept.segments || {});
    const totalAgents = segEntries.reduce((sum, [, seg]) => sum + (seg.n_agents || 0), 0);

    // Aggregate distribution data from segments into concept-level k_scores
    // In multi-concept mode, concept-level k_scores don't have distribution,
    // but segment-level metrics do - we need to aggregate them
    const kScoresWithDistribution: Record<string, KScore> = {};
    const kKeys = K_ORDER.filter(k => concept.k_scores[k]);

    for (const k of kKeys) {
      const baseScore = concept.k_scores[k];
      if (!baseScore) continue;

      // Aggregate distribution from all segments
      const aggregatedDist: Record<string, number> = {};
      let hasAnyDistribution = false;

      for (const [, seg] of segEntries) {
        const segMetric = seg.metrics?.[k];
        if (segMetric?.distribution) {
          hasAnyDistribution = true;
          for (const [rating, count] of Object.entries(segMetric.distribution)) {
            aggregatedDist[rating] = (aggregatedDist[rating] || 0) + (count as number);
          }
        }
      }

      kScoresWithDistribution[k] = {
        ...baseScore,
        distribution: hasAnyDistribution ? aggregatedDist : undefined,
      };
    }

    return {
      test_id: `multi-concept-${concept.index}`,
      concept: {
        product_headline: concept.product_headline,
        brand_name: concept.brand_name,
        price_point: concept.price_point,
        product_image_url: concept.product_image_url,
      },
      population: '',
      n_agents: concept.n_agents || totalAgents,
      timestamp: new Date().toISOString(),
      overall_composite_pct: Math.round(concept.overall_mean * 20), // Convert 5-point to percentage
      k_scores: kScoresWithDistribution,
      segments: concept.segments || {},
      friction_drivers: concept.friction_drivers,
      generated: concept.generated,
      _config: {
        product_image_url: concept.product_image_url,
      },
    };
  }, [concept]);

  const kKeys = K_ORDER.filter(k => report.k_scores[k]);
  const segEntries = Object.entries(report.segments || {})
    .map(([id, seg]) => ({ id, ...seg }))
    .sort((a, b) => (b.metrics?.k1?.pct ?? b.metrics?.k1?.mean ?? 0) - (a.metrics?.k1?.pct ?? a.metrics?.k1?.mean ?? 0));

  return (
    <div className="space-y-10">
      {/* Use same components as single-concept view */}
      <ExecSummary report={report} kKeys={kKeys} />
      {segEntries.length > 0 && <SegmentBreakdown report={report} kKeys={kKeys} segEntries={segEntries} />}
      <FrictionAndDrivers report={report} />
      {report.generated?.overall_insights?.cards && <OverallInsights report={report} />}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   COVER
   ═══════════════════════════════════════════════════ */

const CoverSection: React.FC<{ report: VitaminReport; displayName: string }> = ({ report, displayName }) => {
  const segCount = Object.keys(report.segments || {}).length;
  const productImageUrl = report.concept?.product_image_url || report._config?.product_image_url;
  const hasImage = productImageUrl && productImageUrl.trim() !== '';

  const stats = [
    { value: `N=${report.n_agents}`, label: 'Consumer twins' },
    { value: '1', label: 'Concepts tested' },
    { value: String(segCount), label: 'Segments' },
    { value: String(Object.keys(report.k_scores || {}).length), label: 'Core metrics' },
  ];

  return (
    <div className="rounded-lg shadow-sm overflow-hidden relative" style={{ backgroundColor: BRAND.dark }}>
      {/* Decorative arcs - only show if no image */}
      {!hasImage && (
        <>
          <div
            className="absolute pointer-events-none"
            style={{
              width: 600, height: 600, borderRadius: '50%',
              border: `1px solid ${BRAND.accent}12`,
              top: -200, right: -150,
            }}
          />
          <div
            className="absolute pointer-events-none"
            style={{
              width: 400, height: 400, borderRadius: '50%',
              border: `1px solid ${BRAND.accent}08`,
              top: -80, right: -50,
            }}
          />
        </>
      )}

      <div className="px-8 pt-8 pb-6 relative z-10">
        {/* Header: badge + meta */}
        <div className="flex items-start justify-between mb-10">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.09em] px-3 py-1.5 rounded-full"
              style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
            >
              BluePill AI
            </span>
            {report.concept?.brand_name && (
              <span className="text-xs text-white/40 tracking-wide">× {report.concept.brand_name}</span>
            )}
          </div>
          <div className="text-[11px] text-white/35 text-right leading-relaxed">
            {report.timestamp && <>Prepared: {new Date(report.timestamp).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}<br /></>}
            {report.test_id && <>Study ID: BP-CT-{report.test_id.slice(0, 6)}<br /></>}
            Version: 1.0 — Final
          </div>
        </div>

        {/* Main content area - flex layout for image support */}
        <div className={`flex ${hasImage ? 'gap-8' : ''}`}>
          {/* Left: Title and content */}
          <div className="flex-1">
            {/* Title block */}
            <div className="mb-12">
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-4">CPG Concept Test Report</div>
              <h2 className="text-3xl font-semibold text-white leading-tight mb-3">
                Concept Performance<br />
                <em className="font-normal text-white/40">& Consumer Response</em>
              </h2>
              <p className="text-sm text-white/50 leading-relaxed max-w-2xl">
                Testing: <strong className="text-white/80">{displayName}</strong>
                {report.concept?.product_headline && (
                  <> — {report.concept.product_headline}</>
                )}
              </p>
            </div>

            {/* Stats row */}
            <div className="flex gap-12 pt-6" style={{ borderTop: `1px solid ${BRAND.accent}18` }}>
              {stats.map(s => (
                <div key={s.label}>
                  <div className="text-4xl font-bold leading-none mb-1 tabular-nums" style={{ color: BRAND.accent }}>{s.value}</div>
                  <div className="text-[10px] font-medium uppercase tracking-wider text-white/35">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Product Image (if available) */}
          {hasImage && (
            <div className="flex-shrink-0 w-48 lg:w-56">
              <div
                className="rounded-lg overflow-hidden border-2 h-full flex items-center justify-center p-2"
                style={{ borderColor: `${BRAND.accent}40`, backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <img
                  src={productImageUrl}
                  alt={report.concept?.brand_name || 'Product'}
                  className="max-w-full max-h-48 object-contain rounded"
                  onError={(e) => {
                    // Hide image container if image fails to load
                    (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-8 py-3 flex items-center justify-between gap-4 relative z-10" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] text-white/25 leading-relaxed">
          Produced using BluePill AI&rsquo;s synthetic consumer twin methodology. All responses are AI-simulated personas calibrated to behavioral, attitudinal, and category-specific shopper data.
        </p>
        <span className="text-[9px] uppercase tracking-wider text-white/20 font-semibold whitespace-nowrap">Confidential</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   PIE CHART CARD — For metric distribution visualization
   ═══════════════════════════════════════════════════ */

interface PieChartCardProps {
  metricKey: string;
  score: KScore;
  label: string;
}

// Brand colors for pie chart segments
const PIE_COLORS = [BRAND.accent, BRAND.dark, '#6b7280', '#9ca3af', '#d1d5db'];

// Label mappings for each metric (5 = best, 1 = worst)
const SCORE_LABELS: Record<string, Record<string, string>> = {
  k1: { // Purchase Intent
    '5': 'Definitely Would Buy',
    '4': 'Probably Would Buy',
    '3': 'Might or Might Not Buy',
    '2': 'Probably Would Not Buy',
    '1': 'Definitely Would Not Buy',
  },
  k2: { // Value Perception
    '5': 'Excellent Value',
    '4': 'Good Value',
    '3': 'Fair Value',
    '2': 'Poor Value',
    '1': 'Very Poor Value',
  },
  k3: { // New & Different
    '5': 'Very New & Different',
    '4': 'Somewhat New & Different',
    '3': 'Neutral',
    '2': 'Not Very New & Different',
    '1': 'Not At All New & Different',
  },
  k4: { // Believability
    '5': 'Completely Believable',
    '4': 'Somewhat Believable',
    '3': 'Neutral',
    '2': 'Somewhat Unbelievable',
    '1': 'Not At All Believable',
  },
};

const PieChartCard: React.FC<PieChartCardProps> = ({ metricKey, score, label }) => {
  const labelMap = SCORE_LABELS[metricKey] || {};
  const [hoveredSegment, setHoveredSegment] = useState<{ label: string; count: number; pct: string } | null>(null);

  // Ensure all 5 options are shown with minimum values
  const ensureAllOptions = (dist: Record<string, number>): Record<string, number> => {
    const allRatings = ['5', '4', '3', '2', '1'];
    const MIN_VALUE = 2;
    const result: Record<string, number> = { ...dist };

    // Initialize missing options with 0
    allRatings.forEach(rating => {
      if (result[rating] === undefined) {
        result[rating] = 0;
      }
    });

    // Count how many options need minimum values
    const missingOptions = allRatings.filter(r => result[r] === 0);
    const totalNeeded = missingOptions.length * MIN_VALUE;
    const existingTotal = Object.values(result).reduce((sum, v) => sum + v, 0);

    if (totalNeeded > 0 && existingTotal > totalNeeded) {
      // Distribute from largest values proportionally
      const existingOptions = allRatings.filter(r => result[r] > 0);

      existingOptions.forEach(r => {
        const proportion = result[r] / existingTotal;
        const subtract = Math.floor(proportion * totalNeeded);
        result[r] = Math.max(result[r] - subtract, 1);
      });

      // Add minimum values to missing options
      missingOptions.forEach(r => {
        result[r] = MIN_VALUE;
      });
    } else if (existingTotal === 0) {
      // No data - don't create fake data, just show empty
      return dist;
    }

    return result;
  };

  const distribution = ensureAllOptions(score.distribution || {});

  // Show all entries sorted by score descending (5, 4, 3, 2, 1)
  const entries = Object.entries(distribution).sort((a, b) => Number(b[0]) - Number(a[0]));
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  // Generate pie chart paths with tooltip data
  const generatePiePaths = () => {
    if (total === 0) return [];

    const paths: { d: string; fill: string; label: string; count: number; pct: string }[] = [];
    let currentAngle = -90; // Start from top
    const cx = 60;
    const cy = 60;
    const r = 59;

    entries.forEach(([scoreKey, count], idx) => {
      const angle = (count / total) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      const displayLabel = labelMap[scoreKey] || `Score ${scoreKey}`;
      const pct = ((count / total) * 100).toFixed(0);

      // Handle full circle case
      if (angle >= 360) {
        paths.push({
          d: `M ${cx},${cy} m -${r},0 a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 -${r * 2},0`,
          fill: PIE_COLORS[idx % PIE_COLORS.length],
          label: displayLabel,
          count,
          pct,
        });
      } else {
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy + r * Math.sin(startRad);
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy + r * Math.sin(endRad);

        const largeArc = angle > 180 ? 1 : 0;

        paths.push({
          d: `M ${cx},${cy} L ${x1.toFixed(1)},${y1.toFixed(1)} A ${r},${r} 0 ${largeArc},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`,
          fill: PIE_COLORS[idx % PIE_COLORS.length],
          label: displayLabel,
          count,
          pct,
        });
      }

      currentAngle = endAngle;
    });

    return paths;
  };

  const piePaths = generatePiePaths();

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 flex gap-5">
      {/* Left: Pie chart with hover tooltips */}
      <div className="flex-shrink-0 relative">
        <svg width="120" height="120" viewBox="0 0 120 120" className="cursor-pointer">
          {piePaths.map((path, idx) => (
            <path
              key={idx}
              d={path.d}
              fill={path.fill}
              className="transition-opacity hover:opacity-80"
              onMouseEnter={() => setHoveredSegment({ label: path.label, count: path.count, pct: path.pct })}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          ))}
          {piePaths.length === 0 && (
            <circle cx="60" cy="60" r="59" fill="#e5e7eb" />
          )}
        </svg>
        {/* Custom tooltip */}
        {hoveredSegment && (
          <div
            className="absolute z-50 px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <div className="font-medium">{hoveredSegment.label}</div>
            <div className="text-gray-300">{hoveredSegment.count} responses ({hoveredSegment.pct}%)</div>
          </div>
        )}
      </div>

      {/* Right: Metric label + all options legend */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: BRAND.accent }}>
            {label}
          </div>
          <div className="text-xs font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: BRAND.light, color: BRAND.accent }}>
            Top 2 Box: {(() => {
              const dist = score.distribution || {};
              const top2 = (dist['5'] || 0) + (dist['4'] || 0);
              const t = Object.values(dist).reduce((sum, v) => sum + v, 0);
              return t > 0 ? Math.round((top2 / t) * 100) : 0;
            })()}%
          </div>
        </div>

        {/* All options legend with mapped labels */}
        <div className="space-y-1.5">
          {entries.map(([scoreKey, count], idx) => {
            const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
            const displayLabel = labelMap[scoreKey] || `Score ${scoreKey}`;
            return (
              <div key={scoreKey} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                />
                <div className="flex-1 text-[11px] text-gray-700 leading-tight">{displayLabel}</div>
                <div className="text-[11px] font-medium text-gray-500 tabular-nums">{count} ({pct}%)</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 01 — Executive Summary & Overall Score
   ═══════════════════════════════════════════════════ */

const ExecSummary: React.FC<{ report: VitaminReport; kKeys: string[] }> = ({ report, kKeys }) => {
  const ks = report.k_scores;

  // Get product image URL
  const productImageUrl = report.concept?.product_image_url || report._config?.product_image_url;
  const hasImage = productImageUrl && productImageUrl.trim() !== '';

  return (
    <div>
      <SectionLabel num="01" label="Executive Summary & Overall Score" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Overall concept <span className="italic font-normal" style={{ color: BRAND.accent }}>performance.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Top-line verdict across all segments and metrics before breaking down by individual dimension and audience.
        </p>
      </div>

      {/* Hero row: main score + image + PI + 3 metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 mt-6 mb-6">
        {/* Main score card with image and Purchase Intent */}
        <div className="text-white rounded-lg shadow-sm p-8 flex flex-col justify-between min-h-[280px]" style={{ backgroundColor: BRAND.dark }}>
          <div className="flex justify-between">
            <div className="flex-1">
              {/* Purchase Intent as main metric */}
              {ks.k1 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 mb-3">Purchase Intent</div>
                  <div className="text-7xl font-bold leading-none mb-2 tabular-nums" style={{ color: BRAND.accent }}>
                    {ks.k1.mean?.toFixed(1) || '—'}
                    <span className="text-2xl font-normal text-white/40"> / 5.0</span>
                  </div>
                  <div className="text-sm text-white/60">
                    Top 2 Box: {calculateTop2Box(ks.k1)}%
                  </div>
                </div>
              )}
            </div>
            {/* Product Image */}
            {hasImage && (
              <div className="flex-shrink-0 w-32 ml-4 flex items-start">
                <div className="rounded-lg overflow-hidden border-2 p-2" style={{ borderColor: `${BRAND.accent}40`, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <img
                    src={productImageUrl}
                    alt={report.concept?.brand_name || 'Product'}
                    className="max-w-full max-h-32 object-contain rounded"
                    onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                  />
                </div>
              </div>
            )}
          </div>

          {report.generated?.verdict_text && (
            <div
              className="text-sm leading-relaxed text-white/70 pt-4 mt-4"
              style={{ borderTop: `1px solid ${BRAND.accent}25` }}
            >
              {report.generated.verdict_text}
            </div>
          )}
        </div>

        {/* 3 metric cards (k2, k3, k4 - excluding k1 which is in main card) */}
        <div className="flex flex-col gap-3">
          {kKeys.filter(k => k !== 'k1').map(k => {
            const score = ks[k];
            if (!score) return null;
            return (
              <div key={k} className="rounded-lg border border-gray-200 bg-white p-4 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-[0.08em] mb-1" style={{ color: BRAND.dark }}>
                  {K_LABELS[k] || k}
                </div>
                <div className="text-3xl font-bold leading-none text-gray-900 tabular-nums">
                  {score.mean?.toFixed(1) || '—'}
                  <span className="text-sm font-normal text-gray-400"> / 5.0</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Top 2 Box: {calculateTop2Box(score)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pie Chart Grid - 2x2 layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kKeys.map(k => {
          const score = ks[k];
          if (!score) return null;
          return (
            <PieChartCard
              key={k}
              metricKey={k}
              score={score}
              label={K_LABELS[k] || k}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 02 — Segment Breakdown
   ═══════════════════════════════════════════════════ */

interface SegEntry extends SegmentData { id: string; }

const SegmentBreakdown: React.FC<{ report: VitaminReport; kKeys: string[]; segEntries: SegEntry[] }> = ({ report, kKeys, segEntries }) => {
  const best = segEntries[0];
  const worst = segEntries[segEntries.length - 1];

  // Get best segment's PI score
  const bestPIMean = best?.metrics?.k1?.mean ?? 0;
  const worstPIMean = worst?.metrics?.k1?.mean ?? 0;
  const piGap = Math.abs(bestPIMean - worstPIMean);

  // Find weakest metric using mean scores
  const weakestK = kKeys.reduce((wk, k) => {
    const curr = report.k_scores[k]?.mean ?? 5;
    const prev = report.k_scores[wk]?.mean ?? 5;
    return curr < prev ? k : wk;
  }, kKeys[0]);

  const weakestMean = report.k_scores[weakestK]?.mean;

  return (
    <div>
      <SectionLabel num="02" label="Segment Breakdown" />

      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          How each segment <span className="italic font-normal" style={{ color: BRAND.accent }}>responded.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Segments ranked by purchase intent. &ldquo;Strength&rdquo; shows the highest-scoring metric per segment; &ldquo;primary friction&rdquo; shows where resistance concentrated.
        </p>
      </div>

      {/* Top-line cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-8">
        <div className="rounded-lg shadow-sm p-5" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}30` }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: BRAND.dark }}>Highest Purchase Intent Segment</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">{best?.name || '—'}</div>
          <div className="text-xs" style={{ color: `${BRAND.dark}88` }}>{bestPIMean.toFixed(1)} / 5.0 purchase intent</div>
          <div className="text-xs mt-1" style={{ color: `${BRAND.dark}88` }}>Top 2 Box: {calculateTop2Box(best?.metrics?.k1)}%</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Biggest PI Gap Across Segments</div>
          <div className="text-xl font-bold text-gray-900 leading-tight mb-1">+{piGap.toFixed(1)}</div>
          <div className="text-xs text-gray-400">{best?.name || '—'} vs. {worst?.name || '—'}</div>
          <div className="text-xs text-gray-400 mt-1">Top 2 Box Gap: {Math.abs(calculateTop2Box(best?.metrics?.k1) - calculateTop2Box(worst?.metrics?.k1))}%</div>
        </div>
        <div className="rounded-lg shadow-sm p-5 text-white" style={{ backgroundColor: BRAND.dark }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1">Weakest Metric Overall</div>
          <div className="text-xl font-bold leading-tight mb-1" style={{ color: BRAND.accent }}>{K_LABELS[weakestK] || weakestK}</div>
          <div className="text-xs text-white/40">{weakestMean?.toFixed(1) ?? '—'} / 5.0 across segments</div>
          <div className="text-xs text-white/40 mt-1">Top 2 Box: {calculateTop2Box(report.k_scores[weakestK])}%</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: BRAND.dark }}>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60 w-8">#</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Segment</th>
                {kKeys.map(k => (
                  <th key={k} className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">
                    {(K_LABELS[k] || k).replace('Purchase Intent', 'PI').replace('Value Perception', 'VP').replace('New & Different', 'N&D').replace('Believability', 'Bel.')}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Top Strength</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Primary Friction</th>
                <th className="px-3 py-3 text-left text-[10px] font-medium uppercase tracking-wider text-white/60">Key Insight</th>
              </tr>
            </thead>
            <tbody>
              {segEntries.map((seg, idx) => {
                const segInsight = report.generated?.segment_insights?.[seg.id] || '';

                return (
                  <tr key={seg.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors align-top">
                    <td className="px-3 py-4 text-xs text-gray-400 font-mono tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-4">
                      <div className="font-semibold text-gray-900 text-[13px]">{seg.name}</div>
                      {seg.description && <p className="text-[11px] text-gray-500 leading-snug mt-1">{seg.description}</p>}
                      <div className="text-[10px] text-gray-400 mt-0.5">{seg.n_agents} agents</div>
                    </td>
                    {kKeys.map(k => {
                      const m = seg.metrics?.[k];
                      if (!m) return <td key={k} className="px-3 py-4 text-sm text-gray-300">—</td>;
                      const vs = VERDICT_STYLE[m.verdict] || VERDICT_STYLE.warn;
                      return (
                        <td key={k} className="px-3 py-4">
                          <div className="text-lg font-bold text-gray-900 leading-none mb-1 tabular-nums">
                            {m.mean?.toFixed(1) || '—'}
                            <span className="text-xs font-normal text-gray-400"> / 5</span>
                          </div>
                          <div className="w-14 h-[3px] bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${vs.dotClass}`} style={{ width: `${m.mean ? (m.mean / 5) * 100 : 0}%` }} />
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-4">
                      {seg.top_strength?.label && (
                        <span
                          className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${BRAND.accent}20`, color: BRAND.dark }}
                        >
                          {seg.top_strength.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {seg.primary_friction?.label && (
                        <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                          {seg.primary_friction.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {segInsight && (
                        <p className="text-[11px] text-gray-600 leading-snug">{segInsight}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 03 — Friction & Purchase Drivers
   ═══════════════════════════════════════════════════ */

const FrictionAndDrivers: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const fd = report.friction_drivers || { frictions: [], drivers: [] };
  const frictions = fd.frictions || [];
  const drivers = fd.drivers || [];
  const fdd = report.generated?.friction_driver_descriptions || {};

  return (
    <div>
      <SectionLabel num="03" label="Friction & Purchase Drivers" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What stopped them, <span className="italic font-normal" style={{ color: BRAND.accent }}>and what pulled them in.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Primary friction categories and purchase drivers ranked by frequency of mention across all segments. Percentages reflect share of respondents surfacing each theme.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Friction section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Friction Categories
          </div>
          <div className="space-y-1">
            {frictions.map(f => (
              <div key={f.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {f.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{f.name}</div>
                  {fdd[f.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">{fdd[f.name]}</p>
                  )}
                  <div className="text-[10px] text-gray-400 italic">Mentioned by {f.pct}% of consumers</div>
                </div>
                <div className="text-xl font-bold text-red-600 leading-none min-w-[48px] text-right tabular-nums">{f.pct}%</div>
              </div>
            ))}
            {frictions.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No friction data available</div>
            )}
          </div>
        </div>

        {/* Driver section */}
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-5 pb-3"
            style={{ color: BRAND.dark, borderBottom: `2px solid ${BRAND.accent}30` }}
          >
            Primary Purchase Drivers
          </div>
          <div className="space-y-1">
            {drivers.map(d => (
              <div key={d.rank} className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
                <div className="text-2xl font-bold leading-none min-w-[28px] tabular-nums" style={{ color: `${BRAND.accent}40` }}>
                  {d.rank}
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 mb-1">{d.name}</div>
                  {fdd[d.name] && (
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-1.5">{fdd[d.name]}</p>
                  )}
                  <div className="h-[4px] bg-gray-100 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.min(d.bar_width ?? d.pct, 100)}%`, backgroundColor: BRAND.accent }}
                    />
                  </div>
                </div>
                <div className="text-xl font-bold leading-none min-w-[48px] text-right tabular-nums" style={{ color: BRAND.dark }}>
                  {d.pct}%
                </div>
              </div>
            ))}
            {drivers.length === 0 && (
              <div className="text-sm text-gray-400 italic py-4">No driver data available</div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SECTION 04 — Overall Insights
   ═══════════════════════════════════════════════════ */

const OverallInsights: React.FC<{ report: VitaminReport }> = ({ report }) => {
  const oi = report.generated?.overall_insights;
  if (!oi) return null;

  const cards = oi.cards || [];

  return (
    <div>
      <SectionLabel num="04" label="Overall Insights" />

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          What the data <span className="italic font-normal" style={{ color: BRAND.accent }}>is telling us.</span>
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {cards.map((card, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: BRAND.accent }} />
            <div className="text-4xl font-bold leading-none mb-3 tabular-nums" style={{ color: `${BRAND.accent}40` }}>
              {idx + 1}
            </div>
            <div className="text-[13px] font-semibold text-gray-900 leading-snug mb-2">{card.headline}</div>
            <p className="text-[11.5px] text-gray-500 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {(oi.strongest_signal || oi.biggest_risk) && (
        <div className="rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ backgroundColor: BRAND.accentLight, border: `1px solid ${BRAND.accent}35` }}>
          {oi.strongest_signal && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] mb-2" style={{ color: BRAND.dark }}>Strongest Signal</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">{oi.strongest_signal}</p>
            </div>
          )}
          {oi.biggest_risk && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.09em] text-red-600 mb-2">Biggest Risk</div>
              <p className="text-[13px] text-gray-900 leading-relaxed">{oi.biggest_risk}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VitaminConceptTestResults;
